import React, { useCallback, useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useFormik } from 'formik';
import * as Yup from "yup";
import { useSelector } from "react-redux";
import CheckPermissions, {
    getServiceModulePaths,
    hasWorkflowPermissionForModule,
} from "../authorization/authorization";
import Base64Image from "../components/Base64Image/Base64Image";
import {
  createPdfViewerUrl,
  isPdf,
  normalizeDocumentDataUrl,
} from "./Functions";
import rotateLeftIcon from "../images/Rotate Left.png";
import rotateRightIcon from "../images/Rotate Right.png";
import zoomInIcon from "../images/Zoom In.png";
import zoomOutIcon from "../images/Zoom Out.png";
import "./OpenBase64ImageInPopup.scss";

const OpenBase64ImageInPopup = ({
  docVerifyPopUp,
  hideDocVerifyPopup,
  documentName,
  documentImage,
  backDocumentImage,
  width,
  document,
  documentFlag,
  applicationProgressId,
  objectionStatus,
  updateDocFlag,
  titleToUpdateFlag,
  documentRemarks,
  remarksVariant = "",
  flag_document,
  flag_documentBack,
  parentService,
  verificationPermissionService = 'New Connection',
  verificationPermissionNames = ['Document Verification'],
  readOnly = false,
  allowVerificationDuringObjection = false,
  /** MRC-only: lock dialog size so image load does not resize other services' popups. */
  stableViewer = false,
}) => {
  const [rotationAngle, setRotationAngle] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showRemarksInput, setShowRemarksInput] = useState(false);
  const [frontPdfUrl, setFrontPdfUrl] = useState("");
  const [backPdfUrl, setBackPdfUrl] = useState("");
  const contentRef = useRef(null);
  const { permissions } = useSelector((state) => state.auth);

  const normalizeImageData = (value) => normalizeDocumentDataUrl(value);

  const frontImage = normalizeImageData(documentImage);
  const backImage = normalizeImageData(backDocumentImage);
  const isPdfDocument =
    Boolean(frontImage) && (isPdf(frontImage) || isPdf(documentImage));
  const isBackPdfDocument =
    Boolean(backImage) && (isPdf(backImage) || isPdf(backDocumentImage));
  const isImageDocument =
    Boolean(frontImage) &&
    !isPdfDocument &&
    (frontImage.startsWith("data:image") || frontImage.slice(0, 6) === "data:i");
  const isBackImageDocument =
    Boolean(backImage) &&
    !isBackPdfDocument &&
    (backImage.startsWith("data:image") || backImage.slice(0, 6) === "data:i");

  useEffect(() => {
    let nextFront = "";
    let nextBack = "";

    if (docVerifyPopUp && isPdfDocument && frontImage) {
      nextFront = createPdfViewerUrl(frontImage) || "";
    }
    if (docVerifyPopUp && isBackPdfDocument && backImage) {
      nextBack = createPdfViewerUrl(backImage) || "";
    }

    setFrontPdfUrl(nextFront);
    setBackPdfUrl(nextBack);

    return () => {
      if (nextFront.startsWith("blob:")) URL.revokeObjectURL(nextFront);
      if (nextBack.startsWith("blob:")) URL.revokeObjectURL(nextBack);
    };
  }, [
    docVerifyPopUp,
    frontImage,
    backImage,
    isPdfDocument,
    isBackPdfDocument,
  ]);

  const remarksFormik = useFormik({
    initialValues: { iv_remarks: '' },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object().shape({
      iv_remarks: Yup.string()
        .required("Enter reason for objection.")
        .min(10, "Reason should be between 10 to 200 characters!")
        .max(200, "Reason should be between 10 to 200 characters!"),
    }),
    onSubmit: async (values) => {
      updateDocFlag(titleToUpdateFlag, -1, values.iv_remarks);
    }
  });

  const permissionNames = Array.isArray(verificationPermissionNames)
    ? verificationPermissionNames
    : [verificationPermissionNames || 'Document Verification'];

  const hasVerificationPermission = permissionNames.some((permissionName) =>
    hasWorkflowPermissionForModule(
      permissions,
      getServiceModulePaths(verificationPermissionService),
      "Workflow Permissions",
      permissionName
    )
  );

  const hasWiringTestReportPermission =
    titleToUpdateFlag === "wiringTestReport" &&
    (hasWorkflowPermissionForModule(
      permissions,
      getServiceModulePaths(verificationPermissionService),
      "Workflow Permissions",
      "Wiring Test Report"
    ) ||
      hasVerificationPermission);

  const progressId = Number(applicationProgressId);
  const objection = Number(objectionStatus);

  const canVerifyWiringTestReport =
    titleToUpdateFlag === "wiringTestReport" &&
    (progressId === 2 || progressId === 21);

  const canVerifyAtCurrentStep =
    parentService === "courtOrder"
      ? progressId === 0
      : canVerifyWiringTestReport ||
        (titleToUpdateFlag !== "wiringTestReport" &&
          (progressId === 1 || progressId === 21));

  const showVerificationActions =
    !readOnly &&
    (titleToUpdateFlag === "wiringTestReport"
      ? hasWiringTestReportPermission
      : hasVerificationPermission) &&
    document &&
    (flag_document !== 1 && flag_document !== -1) &&
    canVerifyAtCurrentStep &&
    (objection !== 1 || allowVerificationDuringObjection);

  const resolvedRemarksVariant =
    remarksVariant ||
    (Number(documentFlag) === 1 || Number(flag_document) === 1
      ? "verified"
      : Number(documentFlag) === -1 || Number(flag_document) === -1
        ? "rejected"
        : "");

  const remarksClassName =
    resolvedRemarksVariant === "reuploaded"
      ? "doc-viewer-dialog__remarks doc-viewer-dialog__remarks--reuploaded"
      : resolvedRemarksVariant === "verified"
        ? "doc-viewer-dialog__remarks doc-viewer-dialog__remarks--verified"
        : resolvedRemarksVariant === "rejected"
          ? "doc-viewer-dialog__remarks doc-viewer-dialog__remarks--rejected"
          : "doc-viewer-dialog__remarks";

  const handleClose = () => {
    if (typeof hideDocVerifyPopup === 'function') {
      hideDocVerifyPopup(false);
    }
    setRotationAngle(0);
    setZoomLevel(1);
    setShowRemarksInput(false);
    remarksFormik.resetForm();
  };

  const centerZoomedMediaScroll = useCallback(() => {
    if (!contentRef.current) return;

    const wraps = contentRef.current.querySelectorAll(
      '.doc-viewer-dialog__media-wrap',
    );
    wraps.forEach((wrap) => {
      if (zoomLevel > 1) {
        wrap.scrollLeft = Math.max(0, (wrap.scrollWidth - wrap.clientWidth) / 2);
        wrap.scrollTop = Math.max(0, (wrap.scrollHeight - wrap.clientHeight) / 2);
      } else {
        wrap.scrollLeft = 0;
        wrap.scrollTop = 0;
      }
    });
  }, [zoomLevel]);

  const hasDualImages = Boolean(backDocumentImage);
  const viewerMaxHeight = hasDualImages
    ? Math.round(window.innerHeight * 0.42)
    : Math.round(window.innerHeight * 0.68);

  useEffect(() => {
    if (!docVerifyPopUp) {
      setRotationAngle(0);
      setZoomLevel(1);
      setShowRemarksInput(false);
    }
  }, [docVerifyPopUp]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(centerZoomedMediaScroll);
    });

    return () => cancelAnimationFrame(frameId);
  }, [zoomLevel, rotationAngle, documentImage, backDocumentImage, centerZoomedMediaScroll]);

  return (
    <Dialog
      open={docVerifyPopUp}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      {...(stableViewer ? { transitionDuration: 0 } : {})}
      className={`doc-viewer-dialog${stableViewer ? " doc-viewer-dialog--stable" : ""}`}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      PaperProps={{
        className: 'doc-viewer-dialog__paper',
        sx: { position: 'relative', zIndex: 1 },
      }}
    >
      <DialogTitle className="doc-viewer-dialog__title">
        <div className="doc-viewer-dialog__header">
          <div className="doc-viewer-dialog__heading-wrap">
            <h3 className="doc-viewer-dialog__heading">{documentName}</h3>
            {stableViewer ? (
              <p
                className={`${remarksClassName}${
                  documentRemarks ? "" : " is-placeholder"
                }`}
              >
                {documentRemarks ? `Remarks: ${documentRemarks}` : "\u00a0"}
              </p>
            ) : documentRemarks ? (
              <p className={remarksClassName}>Remarks: {documentRemarks}</p>
            ) : null}
          </div>
          {isImageDocument ? (
            <div className="doc-viewer-dialog__tools">
              <button type="button" className="doc-viewer-dialog__tool-btn" onClick={() => setRotationAngle((angle) => (angle - 90 + 360) % 360)} aria-label="Rotate left">
                <img src={rotateLeftIcon} alt="" />
              </button>
              <button type="button" className="doc-viewer-dialog__tool-btn" onClick={() => setRotationAngle((angle) => (angle + 90) % 360)} aria-label="Rotate right">
                <img src={rotateRightIcon} alt="" />
              </button>
              <button type="button" className="doc-viewer-dialog__tool-btn" onClick={() => setZoomLevel((level) => Math.min(level + 0.1, 3))} aria-label="Zoom in">
                <img src={zoomInIcon} alt="" />
              </button>
              <button type="button" className="doc-viewer-dialog__tool-btn" onClick={() => setZoomLevel((level) => Math.max(level - 0.1, 0.2))} aria-label="Zoom out">
                <img src={zoomOutIcon} alt="" />
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className="doc-viewer-dialog__close"
            onClick={handleClose}
            aria-label="Close document viewer"
          >
            <CloseIcon />
          </button>
        </div>
      </DialogTitle>

      <DialogContent
        ref={contentRef}
        className={`doc-viewer-dialog__content${
          hasDualImages ? ' doc-viewer-dialog__content--dual' : ''
        }`}
      >
        {backDocumentImage ? <p className="doc-viewer-dialog__section-label">Front</p> : null}
        {documentImage && isImageDocument ? (
          <div
            className={`doc-viewer-dialog__media-wrap${
              !stableViewer && (zoomLevel > 1 || rotationAngle !== 0)
                ? " doc-viewer-dialog__media-wrap--zoomed"
                : ""
            }`}
          >
            <Base64Image
              data={frontImage}
              width={width}
              height="auto"
              rotationAngle={rotationAngle}
              zoomLevel={zoomLevel}
              viewerMaxHeight={viewerMaxHeight}
              fitToViewport={stableViewer}
              onLayoutChange={centerZoomedMediaScroll}
            />
          </div>
        ) : documentImage && isPdfDocument && (stableViewer || frontPdfUrl || frontImage) ? (
          <iframe
            src={frontPdfUrl || frontImage || undefined}
            title={documentName || "PDF"}
            className="doc-viewer-dialog__pdf"
            style={
              stableViewer
                ? undefined
                : { height: backDocumentImage ? "420px" : "640px" }
            }
          />
        ) : null}

        {backDocumentImage ? <p className="doc-viewer-dialog__section-label">Back</p> : null}
        {backDocumentImage && isBackImageDocument ? (
          <div
            className={`doc-viewer-dialog__media-wrap${
              !stableViewer && (zoomLevel > 1 || rotationAngle !== 0)
                ? " doc-viewer-dialog__media-wrap--zoomed"
                : ""
            }`}
          >
            <Base64Image
              data={backImage}
              width={width}
              height="auto"
              rotationAngle={rotationAngle}
              zoomLevel={zoomLevel}
              viewerMaxHeight={viewerMaxHeight}
              fitToViewport={stableViewer}
              onLayoutChange={centerZoomedMediaScroll}
            />
          </div>
        ) : backDocumentImage && isBackPdfDocument && (stableViewer || backPdfUrl || backImage) ? (
          <iframe
            src={backPdfUrl || backImage || undefined}
            title={`${documentName || "PDF"} back`}
            className="doc-viewer-dialog__pdf"
            style={stableViewer ? undefined : { height: "420px" }}
          />
        ) : null}
      </DialogContent>

      {showVerificationActions ? (
        <DialogActions className="doc-viewer-dialog__actions">
          {showRemarksInput ? (
            <div className="doc-viewer-dialog__reject-form">
              <div className="doc-viewer-dialog__reject-field">
                <label htmlFor="doc-viewer-remarks">Reason for rejection</label>
                <input
                  id="doc-viewer-remarks"
                  type="text"
                  name="iv_remarks"
                  className="doc-viewer-dialog__remarks-input"
                  value={remarksFormik.values.iv_remarks}
                  onBlur={remarksFormik.handleBlur}
                  onChange={(e) => {
                    remarksFormik.handleChange(e);
                    remarksFormik.setFieldTouched("iv_remarks", true, false);
                  }}
                />
                {remarksFormik.errors.iv_remarks && remarksFormik.touched.iv_remarks ? (
                  <p className="doc-viewer-dialog__field-error">{remarksFormik.errors.iv_remarks}</p>
                ) : null}
              </div>
              <button
                type="button"
                className="doc-viewer-dialog__btn doc-viewer-dialog__btn--accept"
                onClick={remarksFormik.handleSubmit}
              >
                Submit
              </button>
              <button
                type="button"
                className="doc-viewer-dialog__btn doc-viewer-dialog__btn--cancel"
                onClick={() => {
                  remarksFormik.resetForm();
                  setShowRemarksInput(false);
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="doc-viewer-dialog__action-row">
              <button
                type="button"
                className="doc-viewer-dialog__btn doc-viewer-dialog__btn--reject"
                onClick={() => setShowRemarksInput(true)}
              >
                Reject
              </button>
              <button
                type="button"
                className="doc-viewer-dialog__btn doc-viewer-dialog__btn--accept"
                onClick={() => updateDocFlag(titleToUpdateFlag, 1, 'Verified')}
              >
                Accept
              </button>
            </div>
          )}
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default OpenBase64ImageInPopup;